import Vue from 'vue';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { AxiosRequestConfig, AxiosInstance } from 'axios';

import { Auth } from '@/core/authentication/interfaces';
import PromiseHelper from './promise-helper';
import {
    CacheData,
    CacheOptions,
    ServicePromise,
    ServiceResponse
} from './interfaces';

dayjs.extend(duration);

export default class Service extends PromiseHelper {
    storage: Storage;
    constructor(
        readonly api: AxiosInstance,
        readonly auth: Auth | null = null,
        readonly options: CacheOptions = { name: '', cache: false, storage: Vue.$localStorage }
    ) {
        super();

        if (!options.duration) {
            options.duration = {
                days: 1
            };
        }

        this.storage = options.storage || Vue.$localStorage;
    }

    isExpired(creation: string) {
        if (!this.options.duration) {
            return false;
        }

        const ttl = dayjs(creation).add(dayjs.duration(this.options.duration));

        return ttl.isBefore(dayjs());
    }

    fetchCacheData(): CacheData | null {
        if (!this.options.cache || this.options.forceUpdate) {
            return null;
        }

        const storageData = JSON.parse(this.storage.getItem(this.options.name) || '{}');

        if (!storageData.data || this.isExpired(storageData.creation)) {
            return null;
        }

        return storageData;
    }

    insertCacheData(data) {
        if (!this.options.cache) {
            return;
        }

        const item : CacheData = {
            data: data,
            creation: new Date()
        };

        this.storage.setItem(this.options.name, JSON.stringify(item));
    }

    async applyAuth(config?: AxiosRequestConfig) {
        if (!this.auth) {
            return config;
        }

        config = config || {};
        config.headers = config.headers || {};

        await this.auth.init();

        if (this.auth.isValid()) {
            config.headers.Authorization = `${this.auth.scheme} ${this.auth.credentials}`;
        }

        return config;
    }

    getData(url: string, config?: AxiosRequestConfig): ServicePromise<ServiceResponse<any>> {
        const cacheData = this.fetchCacheData();

        if (cacheData) {
            return this.createPromise(cacheData);
        }

        return (async () => {
            let response;

            try {
                config = await this.applyAuth(config);

                response = await this.api.get(url, config) as ServiceResponse;

                response.success = this.isResponseSuccess(response.status);

                this.insertCacheData(response.data);

                return this.successPromise(response);
            } catch (error) {
                return this.failPromise(error.response);
            }
        })();
    }

    postData(url: string, data?: any, config?: AxiosRequestConfig): ServicePromise<ServiceResponse<any>> {
        const cacheData = this.fetchCacheData();

        if (cacheData) {
            return this.createPromise(cacheData);
        }

        return (async () => {
            let response;

            try {
                config = await this.applyAuth(config);

                response = await this.api.post(url, data, config) as ServiceResponse;

                response.success = this.isResponseSuccess(response.status);

                this.insertCacheData(response.data);

                return this.successPromise(response);
            } catch (error) {
                return this.failPromise(error.response);
            }
        })();
    }

    putData(url: string, data?: any, config?: AxiosRequestConfig): ServicePromise<ServiceResponse<any>> {
        return (async () => {
            let response;

            try {
                config = await this.applyAuth(config);

                response = await this.api.put(url, data, config) as ServiceResponse;

                response.success = this.isResponseSuccess(response.status);

                this.insertCacheData(response.data);

                return this.successPromise(response);
            } catch (error) {
                return this.failPromise(error.response);
            }
        })();
    }

    patchData(url: string, data?: any, config?: AxiosRequestConfig): ServicePromise<ServiceResponse<any>> {
        return (async () => {
            let response;

            try {
                config = await this.applyAuth(config);

                response = await this.api.patch(url, data, config) as ServiceResponse;

                response.success = this.isResponseSuccess(response.status);

                this.insertCacheData(response.data);

                return this.successPromise(response);
            } catch (error) {
                return this.failPromise(error.response);
            }
        })();
    }

    deleteData(url: string, config?: AxiosRequestConfig): ServicePromise<ServiceResponse<any>> {
        return (async () => {
            let response;

            try {
                config = await this.applyAuth(config);

                response = await this.api.delete(url, config) as ServiceResponse;

                response.success = this.isResponseSuccess(response.status);

                return this.successPromise(response);
            } catch (error) {
                return this.failPromise(error.response);
            }
        })();
    }
}
